import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import { extractStrategyRows, getActiveStrategies, normalizeStrategies } from '../api/strategyLibraryService';
import './StrategyRadar.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

// ── Constants ────────────────────────────────────────────────────────────────
const FALLBACK_STRATEGIES = [
  { key: 'parabolic', slug: 'parabolic', title: 'Parabolic', icon: '🚀', category: 'bullish' },
  { key: 'bottomfishing', slug: 'bottomfishing', title: 'Bottom Fishing', icon: '🎣', category: 'bullish' },
  { key: 'mfibo', slug: 'mfibo', title: 'M Fibo', icon: '🌀', category: 'bullish' },
  { key: 'exit', slug: 'exit', title: 'Exit', icon: '🚪', category: 'bearish' },
  { key: 'dailyabcandlepositive', slug: 'dailyabcandlepositive', title: 'AB Candle +', icon: '✅', category: 'bullish' },
  { key: 'dailyabcandlenegative', slug: 'dailyabcandlenegative', title: 'AB Candle -', icon: '❌', category: 'bearish' },
  { key: 'fundamental', slug: 'fundamental', title: 'Fundamental', icon: '📐', category: 'bullish' },
  { key: 'rsc', slug: 'rsc', title: 'RSC', icon: '📡', category: 'bullish' },
];

const STRATEGY_TYPES = [
  { value: 'intraday', label: 'Intraday' },
  { value: 'swing', label: 'Swing' },
  { value: 'investment', label: 'Investment' },
  { value: 'btst', label: 'BTST' },
];

const getStrategyTypeLabel = (type) => (
  STRATEGY_TYPES.find(option => option.value === type)?.label || type
);

const LEGACY_DATA_KEYS = {
  parabolic: ['Parabolic'],
  bottomfishing: ['bottomFishing', 'BottomFishing'],
  mfibo: ['fibonacci', 'MFibo'],
  exit: ['Exit'],
  dailyabcandlepositive: ['abPositive', 'DailyABCandlePositive'],
  dailyabcandlenegative: ['abNegative', 'DailyABCandleNegative'],
  fundamental: ['Fundamental'],
  rsc: ['RSC'],
};

const MONTHS = [
  { value: '01', label: 'Jan' }, { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' }, { value: '04', label: 'Apr' },
  { value: '05', label: 'May' }, { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' }, { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' }, { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatCS(val) {
  const a = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (a >= 1e7)  return sign + (a / 1e7).toFixed(2) + 'Cr';
  if (a >= 1e5)  return sign + (a / 1e5).toFixed(2) + 'L';
  if (a >= 1000) return sign + (a / 1000).toFixed(1) + 'K';
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function formatCSFull(val) {
  const sign = val >= 0 ? '+' : '';
  return sign + Math.round(val).toLocaleString('en-IN');
}

function periodLabel(yr, mo, dt) {
  if (dt) {
    const [d, m, y] = dt.split('-');
    return `${d}-${m}-20${y}`;
  }
  if (!yr || !mo) return '—';
  return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${yr}`;
}

function strategyDataKeys(strategy) {
  const keys = [strategy.key, strategy.slug, ...(LEGACY_DATA_KEYS[strategy.slug] || []), ...(LEGACY_DATA_KEYS[strategy.key] || [])];
  return [...new Set(keys.filter(Boolean))];
}

function getStrategyStocks(rawData, strategy) {
  const strategyMap = rawData?.strategies || {};
  const key = strategyDataKeys(strategy).find(k => Array.isArray(strategyMap[k]));
  return key ? strategyMap[key] : [];
}

function toRadarStrategies(rows) {
  return normalizeStrategies(rows).filter(s => s.key || s.slug).map(s => ({
    key: s.key || s.slug,
    slug: s.slug || s.key,
    title: s.title || s.name,
    icon: s.icon || '🎯',
    category: s.category || 'bullish',
    strategyTypes: Array.isArray(s.strategyTypes) ? s.strategyTypes : [],
  }));
}

function formatUpdated(date) {
  if (!date) return '';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// ── SVG Icons ────────────────────────────────────────────────────────────────
function TvIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function CalIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

// ── Component ────────────────────────────────────────────────────────────────
export default function StrategyRadar() {
  // Filter state
  const [allDates, setAllDates]         = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);   // null = monthly view
  const [filter, setFilter]             = useState('all');

  // Data state
  const [rawData, setRawData]           = useState(null);
  const [loading, setLoading]           = useState(false);
  const [refreshing, setRefreshing]     = useState(false);
  const [autoRefresh, setAutoRefresh]   = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [error, setError]               = useState(null);
  const [filterInfo, setFilterInfo]     = useState('Select a period and click Apply');
  const [strategies, setStrategies]     = useState(FALLBACK_STRATEGIES);

  // UI state
  const [selectedStrategy, setSelectedStrategy] = useState(FALLBACK_STRATEGIES[0].key);
  const [sort, setSort]                 = useState('alpha');
  const [stratSearch, setStratSearch]   = useState('');
  const [strategyTypeFilter, setStrategyTypeFilter] = useState('all');
  const [copied, setCopied]             = useState(false);
  const [filterOpen, setFilterOpen]     = useState(false);
  const [chartView, setChartView]       = useState('overview'); // 'overview' | 'detail'
  const [stockSort, setStockSort]       = useState('desc');     // 'desc' | 'asc' | 'alpha'
  const [stockSearch, setStockSearch]   = useState('');
  const stratListRef = useRef(null);
  const lastFetchRef = useRef(null);

  // ── Fetch available dates on mount ────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    getActiveStrategies()
      .then(({ data }) => {
        if (!mounted) return;
        const rows = toRadarStrategies(extractStrategyRows(data));
        if (rows.length) {
          setStrategies(rows);
          setSelectedStrategy(prev => rows.some(strategy => strategy.key === prev) ? prev : rows[0].key);
        }
      })
      .catch(() => {});

    const loadDates = async () => {
      try {
        const { data } = await api.get('/strategy-analysis/dates');
        if (data.success && Array.isArray(data.dates)) {
          setAllDates(data.dates);
          // Sort dates descending to find the latest available date
          const sortedDates = [...data.dates].sort((a, b) => {
            const [da, ma, ya] = a.split('-').map(Number);
            const [db, mb, yb] = b.split('-').map(Number);
            if (ya !== yb) return yb - ya;
            if (ma !== mb) return mb - ma;
            return db - da;
          });
          const latestDate = sortedDates[0];
          if (!latestDate) return;
          const [, latestM, latestY] = latestDate.split('-');
          const yr = `20${latestY}`;
          const mo = latestM; // e.g. '05'
          setSelectedYear(yr);
          setSelectedMonth(mo);
          setSelectedDate(latestDate);          // pre-select the latest date
          doFetch(yr, mo, latestDate, 'daily'); // load latest daily data on first visit
        }
      } catch {
        // silent — user can manually apply
      }
    };
    loadDates();
    return () => { mounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Core fetch ────────────────────────────────────────────────────────────
  const doFetch = async (yr, mo, dt, vm, { silent = false } = {}) => {
    if (!yr) return;
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setError(null);
      setRawData(null);
    }
    lastFetchRef.current = { yr, mo, dt, vm };
    try {
      let data;
      if (vm === 'daily' && dt) {
        ({ data } = await api.get(`/strategy-analysis?date=${dt}`));
      } else {
        ({ data } = await api.get(`/strategy-analysis/month?year=${yr}&month=${parseInt(mo, 10)}`));
      }
      if (data.success) {
        const responseStrategies = toRadarStrategies(extractStrategyRows(data));
        const nextStrategies = responseStrategies.length ? responseStrategies : strategies;
        if (responseStrategies.length) {
          setStrategies(responseStrategies);
          setSelectedStrategy(prev => responseStrategies.some(strategy => strategy.key === prev) ? prev : responseStrategies[0].key);
        }
        setRawData(data);
        setFilterInfo(`${periodLabel(yr, mo, vm === 'daily' ? dt : null)} — ${nextStrategies.length} strategies`);
        setLastUpdatedAt(new Date());
        if (silent) setError(null);
      } else if (!silent) {
        setError(data.error || 'No data returned');
        setFilterInfo('No data for selected period');
      }
    } catch {
      if (!silent) {
        setError('Cannot connect to server');
        setFilterInfo('Connection failed');
      }
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  // ── Auto-refresh so webhook-forwarded stocks appear without manual reload ──
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      if (document.hidden) return;
      const last = lastFetchRef.current;
      if (!last) return;
      doFetch(last.yr, last.mo, last.dt, last.vm, { silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualRefresh = () => {
    const last = lastFetchRef.current;
    if (last) {
      doFetch(last.yr, last.mo, last.dt, last.vm, { silent: true });
      return;
    }
    const vm = selectedDate ? 'daily' : 'monthly';
    doFetch(selectedYear, selectedMonth, selectedDate, vm, { silent: true });
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleYearChange = (yr) => {
    setSelectedYear(yr);
    setSelectedDate(null);
  };

  const handleMonthChange = (mo) => {
    setSelectedMonth(mo);
    setSelectedDate(null);
  };

  const handleDateChange = (dt) => {
    setSelectedDate(dt || null);
  };

  const handleApply = () => {
    const vm = selectedDate ? 'daily' : 'monthly';
    doFetch(selectedYear, selectedMonth, selectedDate, vm);
    setCopied(false);
  };

  const handleReset = () => {
    const cy = new Date().getFullYear().toString();
    const cm = (new Date().getMonth() + 1).toString().padStart(2, '0');
    setSelectedYear(cy);
    setSelectedMonth(cm);
    setSelectedDate(null);
    setFilter('all');
    setSort('alpha');
    setStratSearch('');
    setStrategyTypeFilter('all');
    setSelectedStrategy(strategies[0]?.key || FALLBACK_STRATEGIES[0].key);
    setCopied(false);
    setChartView('overview');
    doFetch(cy, cm, null, 'monthly');
  };

  const handleSelectStrategy = (key) => {
    setSelectedStrategy(key);
    setCopied(false);
    setChartView('detail');
    setTimeout(() => {
      const el = stratListRef.current?.querySelector(`[data-strategy="${key}"]`);
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  };

  const handleBackToOverview = () => {
    setChartView('overview');
  };

  const copySymbols = () => {
    if (!stocks?.length) return;
    const text = stocks.map(s => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Computed data ─────────────────────────────────────────────────────────
  const getYears = () => {
    const years = new Set();
    allDates.forEach(d => { const [,, y] = d.split('-'); years.add(`20${y}`); });
    return [...years].sort((a, b) => b - a);
  };

  const getFilteredDates = () => {
    if (!selectedYear || !selectedMonth) return [];
    return allDates.filter(d => {
      const [, m, y] = d.split('-');
      return `20${y}` === selectedYear && m === selectedMonth;
    });
  };

  const strategySummary = strategies.map(s => {
    const allStocks = getStrategyStocks(rawData, s);
    const filtered = filter === 'smart'
      ? s.category === 'bearish'
        ? allStocks.filter(st => (st.csValue || 0) < 0)
        : allStocks.filter(st => (st.csValue || 0) >= 100)
      : allStocks;
    const totalCS = filtered.reduce((sum, st) => sum + (st.csValue || 0), 0);
    return {
      ...s,
      strategyTypes: Array.isArray(s.strategyTypes) ? s.strategyTypes : [],
      stocks: filtered,
      totalCS,
      count: filtered.length,
    };
  });

  const selectedStratObj = strategySummary.find(s => s.key === selectedStrategy) || null;
  const stocks = rawData ? (selectedStratObj?.stocks ?? []) : null;

  const sortedStocks = stocks ? stocks.slice()
    .filter(st => {
      const q = stockSearch.trim().toLowerCase();
      if (!q) return true;
      return (st.symbol || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (stockSort === 'alpha') return (a.symbol || '').localeCompare(b.symbol || '');
      if (stockSort === 'asc')   return (a.csValue || 0) - (b.csValue || 0);
      return (b.csValue || 0) - (a.csValue || 0); // desc
    }) : null;

  const filteredStratList = strategySummary.filter(s => {
    const matchesSearch = s.title.toLowerCase().includes(stratSearch.trim().toLowerCase());
    const matchesType = strategyTypeFilter === 'all'
      || s.strategyTypes.includes(strategyTypeFilter);
    return matchesSearch && matchesType;
  });

  const stats = rawData ? {
    totalStrategies: strategies.length,
    positive: strategySummary.filter(s => s.totalCS > 0).length,
    negative: strategySummary.filter(s => s.totalCS < 0).length,
    period: periodLabel(selectedYear, selectedMonth, selectedDate),
    totalStocks: strategySummary.reduce((sum, s) => sum + s.count, 0),
  } : null;

  // ── Chart data (overview = all strategies, detail = stocks of selected) ──
  const isDetailView = chartView === 'detail' && selectedStratObj !== null && rawData !== null;

  const chartData = (() => {
    if (!rawData) return null;

    if (isDetailView && selectedStratObj) {
      const sortedStocks = selectedStratObj.stocks
        .slice()
        .sort((a, b) => (b.csValue || 0) - (a.csValue || 0));
      return {
        sortedData: null,
        stockData: sortedStocks,
        chartJs: {
          labels: sortedStocks.map(st => st.symbol),
          datasets: [{
            label: selectedStratObj.title,
            data: sortedStocks.map(st => st.csValue || 0),
            backgroundColor: sortedStocks.map(st =>
              (st.csValue || 0) >= 0 ? 'rgba(0,242,96,0.72)' : 'rgba(255,107,107,0.72)'
            ),
            borderColor: sortedStocks.map(st =>
              (st.csValue || 0) >= 0 ? '#00f260' : '#ff6b6b'
            ),
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
          }],
        },
      };
    }

    // Overview: all strategies
    let sorted = strategySummary.slice();
    if (sort === 'alpha') sorted.sort((a, b) => a.title.localeCompare(b.title));
    else sorted.sort((a, b) => b.totalCS - a.totalCS);
    return {
      sortedData: sorted,
      stockData: null,
      chartJs: {
        labels: sorted.map(s => s.title),
        datasets: [{
          label: 'CS Value',
          data: sorted.map(s => s.totalCS),
          backgroundColor: sorted.map(s => s.totalCS >= 0 ? 'rgba(0,242,96,0.7)' : 'rgba(255,107,107,0.7)'),
          borderColor:     sorted.map(s => s.totalCS >= 0 ? '#00f260' : '#ff6b6b'),
          borderWidth: 1,
          borderRadius: 5,
          borderSkipped: false,
        }],
      },
    };
  })();

  const detailStockCount = isDetailView ? (selectedStratObj?.stocks?.length || 0) : 0;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 350 },
    onClick: (evt, elements) => {
      if (!isDetailView && elements.length > 0 && chartData?.sortedData) {
        handleSelectStrategy(chartData.sortedData[elements[0].index].key);
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,15,30,0.95)',
        borderColor: 'rgba(255,255,255,0.12)',
        borderWidth: 1,
        titleColor: '#fff',
        bodyColor: 'rgba(255,255,255,0.7)',
        callbacks: { label: ctx => ` CS: ${formatCS(ctx.raw)}` },
      },
      datalabels: {
        display: detailStockCount <= 20,
        color: ctx => ctx.dataset.data[ctx.dataIndex] >= 0 ? '#00f260' : '#ff6b6b',
        anchor: ctx => ctx.dataset.data[ctx.dataIndex] >= 0 ? 'end' : 'start',
        align:  ctx => ctx.dataset.data[ctx.dataIndex] >= 0 ? 'top'  : 'bottom',
        font: { size: isDetailView ? 8 : 9, weight: '700' },
        formatter: val => {
          const a = Math.abs(val);
          const sign = val < 0 ? '-' : '';
          if (a >= 1e7) return sign + (a / 1e7).toFixed(1) + 'Cr';
          if (a >= 1e5) return sign + (a / 1e5).toFixed(1) + 'L';
          return val.toFixed(0);
        },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.04)' },
        ticks: {
          color: 'rgba(255,255,255,0.65)',
          font: { size: isDetailView && detailStockCount > 15 ? 8 : 10, weight: '600' },
          maxRotation: isDetailView && detailStockCount > 10 ? 45 : 0,
          minRotation: isDetailView && detailStockCount > 10 ? 45 : 0,
        },
        border: { color: 'rgba(255,255,255,0.1)' },
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: {
          color: 'rgba(255,255,255,0.45)',
          font: { size: 9 },
          callback: v => {
            const a = Math.abs(v);
            if (a >= 1e7) return (v / 1e7).toFixed(1) + 'Cr';
            if (a >= 1e5) return (v / 1e5).toFixed(1) + 'L';
            return v;
          },
        },
        border: { color: 'rgba(255,255,255,0.1)' },
      },
    },
  };

  // ── Calendar URL ──────────────────────────────────────────────────────────
  const calendarUrl = (stock) => {
    const y = selectedYear || new Date().getFullYear();
    const m = selectedMonth ? parseInt(selectedMonth, 10) : (new Date().getMonth() + 1);
    const cats = stock.categories || (stock.category ? [stock.category] : []);
    let col = 'fo';
    if (cats.some(c => c === 'N50' || c === 'nifty50')) col = 'nifty50';
    else if (cats.some(c => c === 'Others' || c === 'others')) col = 'cash';
    return `/cs-calendar?symbol=${encodeURIComponent(stock.symbol)}&year=${y}&month=${m}&collection=${col}`;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout pageBadge="Strategy Radar">
      <div className={`sd-root${filterOpen ? ' filter-open' : ''}`}>

        {!filterOpen && (
          <button
            className="sd-filter-toggle-btn"
            onClick={() => setFilterOpen(true)}
            aria-label="Show filters"
          >
            <span>☰</span>
            <span>Filters</span>
          </button>
        )}

        {/* ── Filter Bar ─────────────────────────────────────────────────── */}
        <div className={`sd-filter-bar${filterOpen ? ' is-open' : ''}`}>
          <div className="sd-filter-group sd-filter-year">
            <span className="sd-filter-label">Year</span>
            <select
              className="sd-filter-select"
              value={selectedYear || ''}
              onChange={e => handleYearChange(e.target.value)}
            >
              {getYears().map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="sd-filter-divider" />

          <div className="sd-filter-group sd-filter-month">
            <span className="sd-filter-label">Month</span>
            <select
              className="sd-filter-select"
              value={selectedMonth || ''}
              onChange={e => handleMonthChange(e.target.value)}
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className="sd-filter-divider" />

          <div className="sd-filter-group sd-filter-date">
            <span className="sd-filter-label">Date</span>
            <select
              className="sd-filter-select sd-filter-select--wide"
              value={selectedDate || ''}
              onChange={e => handleDateChange(e.target.value)}
            >
              <option value="">📊 Monthly View</option>
              {getFilteredDates().map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="sd-filter-divider" />

          <div className="sd-pill-group sd-filter-mode">
            <button
              className={`sd-pill${filter === 'all' ? ' active' : ''}`}
              onClick={() => setFilter('all')}
            >All</button>
            <button
              className={`sd-pill sd-pill--smart${filter === 'smart' ? ' active' : ''}`}
              onClick={() => setFilter('smart')}
            >SMART</button>
          </div>

          <div className="sd-filter-divider" />

          <button className="sd-btn-apply sd-filter-apply" onClick={handleApply}>▶ Apply</button>
          <button className="sd-btn-reset sd-filter-reset" onClick={handleReset}>↺ Reset</button>
          <button
            className="sd-btn-reset sd-filter-refresh"
            onClick={handleManualRefresh}
            disabled={refreshing}
            title="Refresh now"
          >
            {refreshing ? '⟳ Refreshing…' : '⟳ Refresh'}
          </button>

          <button
            type="button"
            className="sd-btn-apply sd-filter-close"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          >
            ✕ Close Filter
          </button>

          <label
            className="sd-filter-info sd-filter-auto"
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
            title="Auto-refresh every 5 minutes"
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5 min)
          </label>

          {loading && <div className="sd-spinner sd-spinner--inline" />}

          <div className="sd-filter-info sd-filter-date-data">
            {filterInfo}
          </div>

          {lastUpdatedAt && (
            <div
              className="sd-filter-info sd-filter-updated"
              title={lastUpdatedAt.toISOString()}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              Last updated: {formatUpdated(lastUpdatedAt)}
              {refreshing ? ' · refreshing…' : ''}
            </div>
          )}
        </div>

        {/* ── 3-Column Body ──────────────────────────────────────────────── */}
        <div className="sd-body">

          {/* ── LEFT: Strategy List ───────────────────────────────────── */}
          <div className="sd-strategy-panel">
            <div className="sd-strategy-panel-header">
              <div className="sd-strategy-panel-title">
                Strategies
                <span className="sd-strategy-count-badge">{strategies.length}</span>
              </div>
              <input
                type="text"
                className="sd-strategy-search"
                placeholder="Search…"
                value={stratSearch}
                onChange={e => setStratSearch(e.target.value)}
              />
              <div className="sd-strategy-type-filters" aria-label="Filter strategies by type">
                <button
                  type="button"
                  className={`sd-strategy-type-filter${strategyTypeFilter === 'all' ? ' active' : ''}`}
                  aria-pressed={strategyTypeFilter === 'all'}
                  onClick={() => setStrategyTypeFilter('all')}
                >
                  All
                </button>
                {STRATEGY_TYPES.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className={`sd-strategy-type-filter${strategyTypeFilter === value ? ' active' : ''}`}
                    aria-pressed={strategyTypeFilter === value}
                    onClick={() => setStrategyTypeFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="sd-strategy-list" ref={stratListRef}>
              {filteredStratList.map(s => {
                const hasCsVal = rawData !== null;
                const pillCls  = !hasCsVal ? '' : s.totalCS > 0 ? 'pos' : s.totalCS < 0 ? 'neg' : '';
                const pillTxt  = hasCsVal ? (s.totalCS > 0 ? '+' : '') + formatCS(s.totalCS) : null;
                const isBearish = s.category === 'bearish';
                return (
                  <div
                    key={s.key}
                    data-strategy={s.key}
                    className={`sd-strategy-item${selectedStrategy === s.key ? ' selected' : ''}${isBearish ? ' bearish' : ''}`}
                    onClick={() => handleSelectStrategy(s.key)}
                  >
                    <div className="sd-strategy-name-wrap">
                      <div className="sd-strategy-name">
                        <span className="sd-strategy-icon">{s.icon}</span>
                        {s.title}
                      </div>
                      {s.strategyTypes.length > 0 && (
                        <div className="sd-strategy-type-tags">
                          {s.strategyTypes.map(type => (
                            <span key={type}>{getStrategyTypeLabel(type)}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="sd-strategy-meta">
                      <span className="sd-strategy-stock-count">{s.count}</span>
                      {pillTxt && <span className={`sd-cs-pill${pillCls ? ` ${pillCls}` : ''}`}>{pillTxt}</span>}
                    </div>
                  </div>
                );
              })}
              {filteredStratList.length === 0 && (
                <div className="sd-strategy-empty">No matching strategies</div>
              )}
            </div>
          </div>

          {/* ── CENTER: Chart + Stats ─────────────────────────────────── */}
          <div className="sd-chart-panel">

            {/* Summary stat tiles — switch between overview and detail */}
            <div className="sd-summary-row">
              {isDetailView && selectedStratObj ? (
                <>
                  <div className="sd-stat-tile t1">
                    <div className="sd-stat-label">Strategy</div>
                    <div className="sd-stat-value small">{selectedStratObj.icon} {selectedStratObj.title}</div>
                  </div>
                  <div className="sd-stat-tile t2">
                    <div className="sd-stat-label">Stocks</div>
                    <div className="sd-stat-value">{selectedStratObj.count}</div>
                  </div>
                  <div className="sd-stat-tile t3">
                    <div className="sd-stat-label">Positive</div>
                    <div className="sd-stat-value">
                      {selectedStratObj.stocks.filter(s => (s.csValue || 0) >= 0).length}
                    </div>
                  </div>
                  <div className="sd-stat-tile t4">
                    <div className="sd-stat-label">Total CS</div>
                    <div className={`sd-stat-value small ${selectedStratObj.totalCS >= 0 ? 'sd-pos' : 'sd-neg'}`}>
                      {formatCS(selectedStratObj.totalCS)}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="sd-stat-tile t1">
                    <div className="sd-stat-label">Strategies</div>
                    <div className="sd-stat-value">{stats ? stats.totalStrategies : '—'}</div>
                  </div>
                  <div className="sd-stat-tile t2">
                    <div className="sd-stat-label">Positive Flow</div>
                    <div className="sd-stat-value">{stats ? stats.positive : '—'}</div>
                  </div>
                  <div className="sd-stat-tile t3">
                    <div className="sd-stat-label">Negative Flow</div>
                    <div className="sd-stat-value">{stats ? stats.negative : '—'}</div>
                  </div>
                  <div className="sd-stat-tile t4">
                    <div className="sd-stat-label">Total Stocks</div>
                    <div className="sd-stat-value">{stats ? stats.totalStocks : '—'}</div>
                  </div>
                </>
              )}
            </div>

            {/* Bar chart */}
            <div className="sd-chart-card">
              <div className="sd-chart-header">
                <div className="sd-chart-title">
                  {isDetailView && selectedStratObj ? (
                    <>
                      <button className="sd-chart-back-btn" onClick={handleBackToOverview}
                        title="Back to all strategies">← All</button>
                      <span className="sd-chart-breadcrumb">
                        {selectedStratObj.icon} {selectedStratObj.title} — Stocks
                      </span>
                      {' '}<span>{stats ? stats.period : ''}</span>
                    </>
                  ) : (
                    <>CS Value by Strategy — <span>{stats ? stats.period : 'select a period'}</span></>
                  )}
                </div>
                {!isDetailView && (
                  <div className="sd-sort-toggle">
                    <button
                      className={`sd-sort-btn${sort === 'alpha' ? ' active' : ''}`}
                      onClick={() => setSort('alpha')}
                    >A–Z</button>
                    <button
                      className={`sd-sort-btn${sort === 'value' ? ' active' : ''}`}
                      onClick={() => setSort('value')}
                    >By Value</button>
                  </div>
                )}
              </div>

              <div className="sd-chart-body">
                {loading && (
                  <div className="sd-chart-loading">
                    <div className="sd-spinner" />
                    <span>Loading…</span>
                  </div>
                )}
                {!loading && error && (
                  <div className="sd-chart-placeholder">
                    <div className="sd-ph-icon">❌</div>
                    <div>{error}</div>
                  </div>
                )}
                {!loading && !error && !chartData && (
                  <div className="sd-chart-placeholder">
                    <div className="sd-ph-icon">📊</div>
                    <div>Select a period and click Apply</div>
                  </div>
                )}
                {!loading && !error && chartData && (
                  <Bar data={chartData.chartJs} options={chartOptions} />
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT: Stock Breakdown ────────────────────────────────── */}
          <div className="sd-table-panel">
            <div className="sd-table-panel-header">
              <div className="sd-table-panel-top">
                <div>
                  <div className="sd-table-panel-label">Stock Breakdown</div>
                  <div className="sd-table-panel-strategy">
                    {selectedStratObj
                      ? <><span className="sd-table-strategy-icon">{selectedStratObj.icon}</span>{selectedStratObj.title}</>
                      : '— select a strategy —'}
                  </div>
                </div>
                <button
                  className={`sd-copy-btn${copied ? ' copied' : ''}`}
                  onClick={copySymbols}
                  disabled={!stocks?.length}
                  title="Copy symbols for TradingView watchlist"
                >
                  {copied ? '✓ Copied!' : '⧉ Copy'}
                </button>
              </div>
              {stocks && (
                <div className="sd-table-panel-meta">
                  {stocks.length} stock{stocks.length !== 1 ? 's' : ''}
                  {selectedStratObj && stocks.length > 0 && (
                    <span className={`sd-table-total ${selectedStratObj.totalCS >= 0 ? 'pos' : 'neg'}`}>
                      {' '}· {formatCSFull(selectedStratObj.totalCS)}
                    </span>
                  )}
                </div>
              )}
              {/* Search stocks */}
              <div className="sd-tbl-search-row">
                <input
                  type="text"
                  className="sd-tbl-search-input"
                  placeholder="🔍 Search symbol…"
                  value={stockSearch}
                  onChange={(e) => setStockSearch(e.target.value)}
                />
                {stockSearch && (
                  <button
                    type="button"
                    className="sd-tbl-search-clear"
                    onClick={() => setStockSearch('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              {/* Sort controls */}
              <div className="sd-tbl-sort-row">
                {[{ key: 'desc', label: '↓ CS' }, { key: 'asc', label: '↑ CS' }, { key: 'alpha', label: 'A–Z' }].map(opt => (
                  <button
                    key={opt.key}
                    className={`sd-tbl-sort-btn${stockSort === opt.key ? ' active' : ''}`}
                    onClick={() => setStockSort(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="sd-table-scroll">
              <table className="sd-stocks-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th className="right">CS Value</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={2}>
                        <div className="sd-table-empty">
                          <div className="sd-spinner" style={{ margin: '0 auto 6px' }} />
                          Loading…
                        </div>
                      </td>
                    </tr>
                  ) : !rawData ? (
                    <tr>
                      <td colSpan={2}>
                        <div className="sd-table-empty">
                          <div className="sd-table-empty-icon">📊</div>
                          Apply a filter to view data
                        </div>
                      </td>
                    </tr>
                  ) : stocks?.length === 0 ? (
                    <tr>
                      <td colSpan={2}>
                        <div className="sd-table-empty">
                          <div className="sd-table-empty-icon">📭</div>
                          No data for this strategy
                        </div>
                      </td>
                    </tr>
                  ) : sortedStocks?.length === 0 ? (
                    <tr>
                      <td colSpan={2}>
                        <div className="sd-table-empty">
                          <div className="sd-table-empty-icon">🔍</div>
                          No stocks match "{stockSearch}"
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedStocks.map((stock, i) => {
                      const cats = stock.categories || (stock.category ? [stock.category] : []);
                      const isPos = (stock.csValue || 0) >= 0;
                      return (
                        <tr key={i}>
                          <td>
                            <div className="sd-sym-cell">
                              <div className="sd-sym-row">
                                <span className="sd-sym-name">{stock.symbol}</span>
                                <span className="sd-sym-icons">
                                  <a
                                    href={`https://in.tradingview.com/chart/?symbol=NSE%3A${stock.symbol}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`${stock.symbol} on TradingView`}
                                    className="sd-icon-link sd-icon-tv"
                                  >
                                    <TvIcon />
                                  </a>
                                  <a
                                    href={calendarUrl(stock)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`${stock.symbol} CS Calendar`}
                                    className="sd-icon-link sd-icon-cal"
                                  >
                                    <CalIcon />
                                  </a>
                                </span>
                              </div>
                              {cats.length > 0 && (
                                <div className="sd-cat-badges">
                                  {cats.map(cat => (
                                    <span key={cat} className={`sd-cat sd-cat--${cat.toLowerCase().replace(/&/g, '')}`}>
                                      {String(cat).toLowerCase() === 'others' ? 'CASH' : cat}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className={`right ${isPos ? 'sd-td-pos' : 'sd-td-neg'}`}>
                            {formatCSFull(stock.csValue || 0)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
